import { useCallback, useEffect, useRef } from 'react';

const RESTORE_WINDOW_MS = 5000;
const RESTORE_INTERVAL_MS = 100;
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

let restoredThisPageLoad = false;

// Depending on screen width, either the page or the dashboard layout is what scrolls
const getScrollElements = () => [
  document.scrollingElement,
  document.querySelector('.layout-dashboard'),
];

const getStorageKey = (scopeKey) => `account-page-scroll:${scopeKey}`;

const isPageReload = () => {
  try {
    return window.performance.getEntriesByType('navigation')[0]?.type === 'reload';
  } catch (error) {
    return false;
  }
};

const readPositions = (scopeKey) => {
  try {
    return JSON.parse(window.sessionStorage.getItem(getStorageKey(scopeKey)));
  } catch (error) {
    return null;
  }
};

/**
 * Remembers how far each scope (a user's tab) was scrolled, and returns a function that
 * goes back to a scope's saved position. It also restores the position after a browser
 * refresh. Sections load their data at different times, so a position is re-applied for a
 * few seconds while the page grows, and the user scrolling or clicking takes over
 * immediately. Nothing is saved meanwhile, because a shorter tab pulling the page up would
 * otherwise overwrite the position being restored.
 */
export default function useScrollRestoration(scopeKey) {
  const stopRestoring = useRef(null);
  // Set while switching to a scope with nothing saved, so scrolling the caller does before
  // the switch completes isn't saved against the scope being left
  const switchingScope = useRef(false);

  const restorePosition = useCallback((targetScopeKey) => {
    stopRestoring.current?.();

    const positions = readPositions(targetScopeKey);
    if (!positions) {
      switchingScope.current = true;
      return false;
    }

    const deadline = Date.now() + RESTORE_WINDOW_MS;
    let scrollsAwayInARow = 0;

    // Some browsers don't report dragging the scrollbar as a mouse event, so also stop when
    // the page keeps moving away from the target. A page still too short to reach the
    // target sits at its bottom, which isn't treated as moving away.
    const onScroll = () => {
      const movedAway = getScrollElements().some((element, index) => (
        element
        && positions[index] != null
        && element.scrollTop !== positions[index]
        && element.scrollTop < element.scrollHeight - element.clientHeight - 1
      ));
      scrollsAwayInARow = movedAway ? scrollsAwayInARow + 1 : 0;
      if (scrollsAwayInARow >= 2) stop();
    };

    const stop = () => {
      clearInterval(timer);
      USER_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, stop));
      document.removeEventListener('scroll', onScroll, { capture: true });
      stopRestoring.current = null;
    };

    const applyPositions = () => {
      if (Date.now() > deadline) {
        stop();
        return;
      }
      getScrollElements().forEach((element, index) => {
        if (element && element.scrollTop !== positions[index]) {
          element.scrollTop = positions[index];
        }
      });
    };

    const timer = setInterval(applyPositions, RESTORE_INTERVAL_MS);
    USER_SCROLL_EVENTS.forEach((type) => window.addEventListener(type, stop, { passive: true }));
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    stopRestoring.current = stop;
    applyPositions();

    return true;
  }, []);

  useEffect(() => {
    switchingScope.current = false;

    const savePosition = () => {
      if (stopRestoring.current || switchingScope.current) return;
      try {
        const positions = getScrollElements().map((element) => element?.scrollTop || 0);
        window.sessionStorage.setItem(getStorageKey(scopeKey), JSON.stringify(positions));
      } catch (error) {
        // The page still works if storage is unavailable, it just won't restore the position
      }
    };

    // Capture so scrolling inside the layout is seen as well as scrolling the page
    document.addEventListener('scroll', savePosition, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', savePosition, { capture: true });
  }, [scopeKey]);

  useEffect(() => {
    // Only the first load of the page restores by itself, opening a user normally must not jump
    if (!restoredThisPageLoad) {
      restoredThisPageLoad = true;
      if (isPageReload()) restorePosition(scopeKey);
    }

    return () => stopRestoring.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return restorePosition;
}
